import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Diversity3,
    Edit as EditIcon,
    Groups2,
    MailOutline,
    Public,
    Savings,
    StarRate,
    StarBorder,
    SupportAgent,
    Update,
    Verified,
} from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useHriBenefits from "../../hooks/useHriBenefits";

export const HRI_BENEFIT_ICON_OPTIONS = [
    { value: "Diversity3", label: "Auswahl", Icon: Diversity3 },
    { value: "HotelClass", label: "Bewertungen", Icon: StarRate },
    { value: "Groups2", label: "Kontakte", Icon: Groups2 },
    { value: "Update", label: "Aktuell", Icon: Update },
    { value: "StarBorder", label: "Stern", Icon: StarBorder },
    { value: "Verified", label: "Geprüft", Icon: Verified },
    { value: "SupportAgent", label: "Support", Icon: SupportAgent },
    { value: "MailOutline", label: "Newsletter", Icon: MailOutline },
    { value: "Savings", label: "Angebote", Icon: Savings },
    { value: "Public", label: "International", Icon: Public },
];

const blank = {
    icon_name: "StarBorder",
    title_de: "",
    title_en: "",
    title_fr: "",
    title_it: "",
    text_de: "",
    text_en: "",
    text_fr: "",
    text_it: "",
    sort_order: 0,
    is_active: true,
};

const languages = [
    { key: "de", label: "Deutsch" },
    { key: "en", label: "Englisch" },
    { key: "fr", label: "Französisch" },
    { key: "it", label: "Italienisch" },
];

export function getHriBenefitIcon(name) {
    return HRI_BENEFIT_ICON_OPTIONS.find((item) => item.value === name)?.Icon || StarBorder;
}

export default function HriBenefitsPage() {
    const { benefits, loading, error, fetchBenefits, createBenefit, updateBenefit, deleteBenefit } = useHriBenefits();
    const [open, setOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [form, setForm] = React.useState(blank);
    const [langTab, setLangTab] = React.useState("de");
    const [submitError, setSubmitError] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    const rows = React.useMemo(() => {
        return [...(benefits || [])].sort((a, b) => (
            Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
            String(a.title_de || "").localeCompare(String(b.title_de || ""), "de")
        ));
    }, [benefits]);

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setLangTab("de");
        setSubmitError("");
        setOpen(true);
    };

    const openEdit = (benefit) => {
        setEditing(benefit);
        setForm({ ...blank, ...benefit, is_active: benefit.is_active !== undefined ? !!benefit.is_active : true });
        setLangTab("de");
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {
        if (!(form.title_de || "").trim()) {
            setSubmitError("Titel (DE) ist Pflichtfeld.");
            return;
        }

        const payload = {
            ...form,
            title_de: (form.title_de || "").trim(),
            title_en: (form.title_en || "").trim(),
            title_fr: (form.title_fr || "").trim(),
            title_it: (form.title_it || "").trim(),
            text_de: (form.text_de || "").trim(),
            text_en: (form.text_en || "").trim(),
            text_fr: (form.text_fr || "").trim(),
            text_it: (form.text_it || "").trim(),
            sort_order: Number(form.sort_order || 0),
            is_active: !!form.is_active,
        };

        setSubmitting(true);
        setSubmitError("");
        try {
            if (editing) await updateBenefit(editing.id, payload);
            else await createBenefit(payload);
            setOpen(false);
        } catch (e) {
            setSubmitError(e?.response?.data?.error || e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageContainer
            title="HRI Vorteile"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchBenefits} disabled={loading}>Aktualisieren</Button>
                    <Button variant="contained" onClick={openCreate}>Neuer Vorteil</Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper} sx={{ maxHeight: "70vh", overflow: "auto" }}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Icon</TableCell>
                                <TableCell>Titel (DE)</TableCell>
                                <TableCell>Text (DE)</TableCell>
                                <TableCell>Aktiv</TableCell>
                                <TableCell>Sortierung</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((benefit) => {
                                const Icon = getHriBenefitIcon(benefit.icon_name);
                                return (
                                    <TableRow key={benefit.id} hover>
                                        <TableCell><Icon sx={{ color: "#ff8a18" }} /></TableCell>
                                        <TableCell>{benefit.title_de}</TableCell>
                                        <TableCell>{benefit.text_de || "-"}</TableCell>
                                        <TableCell>{benefit.is_active ? "Ja" : "Nein"}</TableCell>
                                        <TableCell>{benefit.sort_order ?? 0}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => openEdit(benefit)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton color="error" onClick={() => { setDeleteTarget(benefit); setDeleteError(""); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? "Vorteil bearbeiten" : "Neuen Vorteil anlegen"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Material Icon</InputLabel>
                            <Select
                                label="Material Icon"
                                value={form.icon_name || "StarBorder"}
                                onChange={(e) => setForm((f) => ({ ...f, icon_name: e.target.value }))}
                            >
                                {HRI_BENEFIT_ICON_OPTIONS.map(({ value, label, Icon }) => (
                                    <MenuItem key={value} value={value}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Icon fontSize="small" sx={{ color: "#ff8a18" }} />
                                            {label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Tabs value={langTab} onChange={(_, value) => setLangTab(value)}>
                            {languages.map((lang) => <Tab key={lang.key} value={lang.key} label={lang.label} />)}
                        </Tabs>

                        {languages.map((lang) => (
                            <Box key={lang.key} sx={{ display: langTab === lang.key ? "grid" : "none", gap: 2 }}>
                                <TextField
                                    label={`Titel (${lang.key.toUpperCase()})${lang.key === "de" ? "*" : ""}`}
                                    value={form[`title_${lang.key}`] || ""}
                                    onChange={(e) => setForm((f) => ({ ...f, [`title_${lang.key}`]: e.target.value }))}
                                />
                                <TextField
                                    label={`Text (${lang.key.toUpperCase()})`}
                                    value={form[`text_${lang.key}`] || ""}
                                    multiline
                                    minRows={3}
                                    onChange={(e) => setForm((f) => ({ ...f, [`text_${lang.key}`]: e.target.value }))}
                                />
                            </Box>
                        ))}

                        <TextField label="Sortierung" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
                            label="Aktiv"
                        />
                    </Box>
                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>{submitting ? "Speichern..." : "Speichern"}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Vorteil löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? <Alert severity="error">{deleteError}</Alert> : (
                        <Alert severity="warning">Willst du <b>{deleteTarget?.title_de}</b> wirklich löschen?</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await deleteBenefit(deleteTarget.id);
                                setDeleteTarget(null);
                            } catch (e) {
                                setDeleteError(e?.response?.data?.error || e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen..." : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
